import { useMemo } from 'react';

import { ExpandCollapseProvider } from '@/Pages/Graph/src/components/debug/common/expandCollapseContext';
import { ForeignDataTreeView } from '@/Pages/Graph/src/components/debug/common/foreign-data-tree';
import {
  RenderModeContext,
  useRenderModeCtx,
} from '@/Pages/Graph/src/control/state';
import {
  foreignDataToTree,
  parseForeignData,
} from '@/Pages/Graph/src/data/foreign-data';

import { useFileUploadTemplateStore } from '../hooks/useFileUploadTemplateStore';
import { applyKeyMappings, extractFieldsWithFinalKeys } from '../utils';

export function DatasetRowPreview() {
  const renderModeState = useRenderModeCtx({ renderMode: 'pretty' });
  const fileMimeType = useFileUploadTemplateStore(
    (state) => state.fileMimeType
  );
  const filePreview = useFileUploadTemplateStore((state) => state.filePreview);
  const inputKeys = useFileUploadTemplateStore((state) => state.inputKeys);
  const outputKeys = useFileUploadTemplateStore((state) => state.outputKeys);
  const metadataKeys = useFileUploadTemplateStore(
    (state) => state.metadataKeys
  );
  const inputKeyMappings = useFileUploadTemplateStore(
    (state) => state.inputKeyMappings
  );
  const outputKeyMappings = useFileUploadTemplateStore(
    (state) => state.outputKeyMappings
  );
  const metadataKeyMappings = useFileUploadTemplateStore(
    (state) => state.metadataKeyMappings
  );

  const inputPreview = useMemo(() => {
    const extracted = extractFieldsWithFinalKeys(
      filePreview?.[0],
      inputKeys,
      fileMimeType === 'text/csv'
    );
    return applyKeyMappings(extracted, inputKeys, inputKeyMappings);
  }, [inputKeys, filePreview, fileMimeType, inputKeyMappings]);

  const outputPreview = useMemo(() => {
    const extracted = extractFieldsWithFinalKeys(
      filePreview?.[0],
      outputKeys,
      fileMimeType === 'text/csv'
    );
    return applyKeyMappings(extracted, outputKeys, outputKeyMappings);
  }, [outputKeys, filePreview, fileMimeType, outputKeyMappings]);

  const metadataPreview = useMemo(() => {
    const extracted = extractFieldsWithFinalKeys(
      filePreview?.[0],
      metadataKeys,
      fileMimeType === 'text/csv'
    );
    return applyKeyMappings(extracted, metadataKeys, metadataKeyMappings);
  }, [metadataKeys, filePreview, fileMimeType, metadataKeyMappings]);

  return (
    <div className="flex max-w-full flex-col gap-4 break-words">
      <div>
        <h2 className="text-md font-semibold">Example Preview</h2>
        <div className="text-sm text-quaternary">
          Here's how your selected input, output and metadata fields will
          appear. The input is what the model sees. The output is what you want
          it to generate. Metadata is additional information attached to the
          example.
        </div>
      </div>
      <RenderModeContext.Provider value={renderModeState}>
        <ExpandCollapseProvider localStorageKey="ls:dataset-row-preview">
          <div className="flex max-w-full flex-col overflow-x-auto">
            <h2 className="mb-2 text-sm font-semibold">Inputs</h2>
            <div className="min-h-8 rounded-md bg-secondary p-2">
              <ForeignDataTreeView
                data={foreignDataToTree({
                  data: parseForeignData(inputPreview),
                })}
              />
            </div>
          </div>
          <div className="flex max-w-full flex-col overflow-x-auto">
            <h2 className="mb-2 text-sm font-semibold">Outputs</h2>
            <div className="min-h-8 rounded-md bg-secondary p-2">
              <ForeignDataTreeView
                data={foreignDataToTree({
                  data: parseForeignData(outputPreview),
                })}
              />
            </div>
          </div>
          <div className="flex max-w-full flex-col overflow-x-auto">
            <h2 className="mb-2 text-sm font-semibold">Metadata</h2>
            <div className="min-h-8 rounded-md bg-secondary p-2">
              <ForeignDataTreeView
                data={foreignDataToTree({
                  data: parseForeignData(metadataPreview),
                })}
              />
            </div>
          </div>
        </ExpandCollapseProvider>
      </RenderModeContext.Provider>
    </div>
  );
}
